/**
 * V2 Carousel Banner — Swiper + stories-style progress timer
 * Figma: section-carousel banner (node 240:2525)
 */
(function () {
  const SECTION_SELECTOR = '[data-v2-carousel-banner]';

  function pad2(n) {
    return String(n).padStart(2, '0');
  }

  function initCarouselBanner(root) {
    if (!root || root.dataset.v2CarouselReady === 'true') return;
    if (typeof Swiper === 'undefined') return;

    const swiperEl = root.querySelector('[data-v2-carousel-swiper]');
    const slides = root.querySelectorAll('.swiper-slide');
    const slideCount = slides.length;
    if (!swiperEl || slideCount === 0) return;

    root.dataset.v2CarouselReady = 'true';

    const timerEnabled = root.dataset.timer === 'true' && slideCount > 1;
    const counterCurrent = root.querySelector('[data-v2-carousel-current]');
    const counterTotal = root.querySelector('[data-v2-carousel-total]');
    const segments = Array.from(root.querySelectorAll('[data-v2-carousel-segment]'));

    let isPaused = false;
    let isHolding = false;
    let activePointerId = null;
    let lastSyncedIndex = -1;
    let swiper = null;

    function setSegmentState(activeIndex) {
      segments.forEach((segment, index) => {
        segment.classList.toggle('is-active', index === activeIndex);
        segment.classList.toggle('is-complete', index < activeIndex);
      });
    }

    function updateCounter(activeIndex) {
      if (counterCurrent) {
        counterCurrent.textContent = pad2(activeIndex + 1);
      }
      if (counterTotal) {
        counterTotal.textContent = pad2(slideCount);
      }
    }

    function prefersReducedMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    function isDisplayed(el) {
      return el.getClientRects().length > 0;
    }

    function syncVideos(activeIndex) {
      const playAllowed = !prefersReducedMotion();
      slides.forEach((slide, index) => {
        slide.querySelectorAll('video').forEach((video) => {
          const onActiveSlide = index === activeIndex;
          const onVisibleBreakpoint = isDisplayed(video);
          if (onActiveSlide && onVisibleBreakpoint && playAllowed) {
            const playPromise = video.play();
            if (playPromise && typeof playPromise.catch === 'function') {
              playPromise.catch(function () {});
            }
          } else {
            if (!video.paused) video.pause();
            if (!onActiveSlide) {
              try {
                video.currentTime = 0;
              } catch (e) {
                /* ignore if metadata is not loaded yet */
              }
            }
          }
        });
      });
    }

    function syncChrome(activeIndex) {
      const index = Math.max(0, Math.min(slideCount - 1, activeIndex || 0));
      updateCounter(index);
      setSegmentState(index);
      syncVideos(index);
      return index;
    }

    function stopTimer() {
      root.classList.remove('is-filling');
    }

    function restartFill() {
      stopTimer();
      void root.offsetWidth;
      if (!timerEnabled) return;
      if (isHolding) {
        isPaused = true;
        root.classList.add('is-paused');
        return;
      }
      isPaused = false;
      root.classList.remove('is-paused');
      root.classList.add('is-filling');
    }

    function pauseTimer() {
      if (!timerEnabled) return;
      isPaused = true;
      root.classList.add('is-paused');
    }

    function resumeTimer() {
      if (!timerEnabled || isHolding) return;
      isPaused = false;
      root.classList.remove('is-paused');
      if (!root.classList.contains('is-filling')) {
        root.classList.add('is-filling');
      }
    }

    function resetAndStart() {
      restartFill();
    }

    function onFillAnimationEnd(event) {
      if (event.animationName !== 'v2-carousel-banner-progress') return;
      if (!event.target.classList.contains('v2-carousel-banner__segment-fill')) return;
      if (!root.classList.contains('is-filling')) return;
      if (isPaused || isHolding) return;
      if (swiper) swiper.slideNext();
    }

    /**
     * Keep progress segments + counter locked to Swiper's realIndex.
     * @param {object} options
     * @param {boolean} [options.restartTimer] - reset timer when index changed
     * @param {boolean} [options.force] - sync even if index unchanged
     */
    function syncFromSwiper(options) {
      if (!swiper) return;
      const restartTimer = options && options.restartTimer;
      const force = options && options.force;
      const index = syncChrome(swiper.realIndex);
      const changed = index !== lastSyncedIndex;

      if (changed || force) {
        lastSyncedIndex = index;
      }

      if (changed && restartTimer) {
        resetAndStart();
      } else if (!changed && restartTimer === false) {
        // no-op
      }

      return changed;
    }

    function beginHold(event) {
      if (slideCount < 2 || !timerEnabled) return;
      if (event && event.target && event.target.closest && event.target.closest('a, button')) return;
      if (event && event.pointerType === 'mouse' && typeof event.button === 'number' && event.button !== 0) {
        return;
      }
      isHolding = true;
      pauseTimer();
    }

    function onIndexChanged() {
      syncFromSwiper({ restartTimer: true });
    }

    swiper = new Swiper(swiperEl, {
      slidesPerView: 1,
      speed: 400,
      // rewind avoids loop clone slides, which desync realIndex vs progress UI
      loop: false,
      rewind: slideCount > 1,
      allowTouchMove: slideCount > 1,
      simulateTouch: slideCount > 1,
      grabCursor: slideCount > 1,
      threshold: 5,
      on: {
        init(instance) {
          swiper = instance;
          lastSyncedIndex = -1;
          syncFromSwiper({ restartTimer: true, force: true });
        },
        realIndexChange: onIndexChanged,
        slideChange: onIndexChanged,
        slideChangeTransitionEnd: onIndexChanged,
        // After a slow drag, index can settle here even if slideChange was skipped
        touchEnd() {
          window.requestAnimationFrame(function () {
            syncFromSwiper({ restartTimer: true });
            if (!isHolding) {
              // If index did not change, resume paused timer from remaining time
              if (isPaused) resumeTimer();
            }
          });
        },
      },
    });

    function onPointerDown(event) {
      if (event.target.closest('a, button')) return;
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      activePointerId = event.pointerId;
      beginHold(event);
    }

    function onPointerUp(event) {
      if (activePointerId != null && event.pointerId !== activePointerId) return;
      activePointerId = null;
      isHolding = false;

      // Wait a frame so Swiper can commit the new realIndex after a slow swipe
      window.requestAnimationFrame(function () {
        const changed = syncFromSwiper({ restartTimer: true });
        if (!changed && isPaused) {
          resumeTimer();
        }
      });
    }

    root.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('pointerup', onPointerUp);
    window.addEventListener('pointercancel', onPointerUp);
    root.addEventListener('animationend', onFillAnimationEnd);

    const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    function onViewportOrMotionChange() {
      if (!swiper) return;
      syncVideos(swiper.realIndex);
    }
    window.addEventListener('resize', onViewportOrMotionChange);
    if (typeof motionQuery.addEventListener === 'function') {
      motionQuery.addEventListener('change', onViewportOrMotionChange);
    } else if (typeof motionQuery.addListener === 'function') {
      motionQuery.addListener(onViewportOrMotionChange);
    }

    root._v2CarouselDestroy = function destroy() {
      stopTimer();
      root.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('pointerup', onPointerUp);
      window.removeEventListener('pointercancel', onPointerUp);
      root.removeEventListener('animationend', onFillAnimationEnd);
      window.removeEventListener('resize', onViewportOrMotionChange);
      if (typeof motionQuery.removeEventListener === 'function') {
        motionQuery.removeEventListener('change', onViewportOrMotionChange);
      } else if (typeof motionQuery.removeListener === 'function') {
        motionQuery.removeListener(onViewportOrMotionChange);
      }
      slides.forEach((slide) => {
        slide.querySelectorAll('video').forEach((video) => {
          video.pause();
        });
      });
      if (swiper && typeof swiper.destroy === 'function') {
        swiper.destroy(true, true);
      }
      delete root.dataset.v2CarouselReady;
      delete root._v2CarouselDestroy;
    };
  }

  function initAll() {
    document.querySelectorAll(SECTION_SELECTOR).forEach(initCarouselBanner);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const root = event.target.querySelector(SECTION_SELECTOR) || event.target.closest(SECTION_SELECTOR);
    if (root) initCarouselBanner(root);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.querySelector(SECTION_SELECTOR) || event.target.closest(SECTION_SELECTOR);
    if (root && typeof root._v2CarouselDestroy === 'function') {
      root._v2CarouselDestroy();
    }
  });

  if (typeof Swiper === 'undefined') {
    const retry = window.setInterval(() => {
      if (typeof Swiper !== 'undefined') {
        window.clearInterval(retry);
        initAll();
      }
    }, 50);
    window.setTimeout(() => window.clearInterval(retry), 5000);
  }
})();
